import { useState } from 'react';
import { Button, Alert } from 'antd';
import { CalendarOutlined } from '@ant-design/icons';
import { useScreenSize } from '@insight-health-ai/roseate/utils';
import { SupaDemo } from 'src/components/groupComponents/supaDemo';
import { ReclaimModal } from 'src/components/groupComponents/Reclaim.Modal';
import { UpgradeModal } from 'src/modules/ambient_solo/components/Payments/UpgradeModal';
import {
  useGetProTrialEntitlement,
  useGetCurrentPractitionerEntitlements,
} from 'src/hooks/settings';
import { ProIcon } from 'src/icons/Pro.icon';
import { CallIcon } from 'src/icons/Call.icon';
import { useAuth, useFeatureEntitlements } from '@frontegg/react';
import { analytics } from 'src/services/analytics';
import { useSelector } from 'react-redux';
import { usePermissionEntitlements } from '@frontegg/react';
import { isOPUpgradeModalVisibleSlice } from 'src/modules/ambient_solo/store/slices/encounter';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';

const remainingDaysToNextMonth = 30 - new Date().getDate();

const gradientButtonClass =
  'text-white bg-gradient-to-br from-pink-500 to-blue-700 bg-origin-border bg-clip-padding flex items-center p-2 rounded-xl hover:shadow-xl font-medium px-2 lg:px-4 gap-2';

export const DemoControls = () => {
  const dispatch = useDispatch();
  const { quota, timeQuota } = useSelector<any, any>(
    (state) => state?.quotaState
  );
  const { pathname } = useLocation();
  const { user } = useAuth();
  const { mediaMax } = useScreenSize();
  const isMobileView = mediaMax('lg');
  const [OpenReclaim, setOpenReclaim] = useState(false);
  const [isUpgradeModalVisible, setIsUpgradeModalVisible] = useState(false);
  const { isEntitled: isBookDemoFeatureEntitled } =
    useFeatureEntitlements('book-demo');
  const { isEntitled: isUpgradePlanFeatureEntitled } =
    useFeatureEntitlements('upgrade-plan');
  const { isEntitled: canCreateEncounter } =
    usePermissionEntitlements('encounter.create');

  const { isAuraPro, isAuraProTrial, auraProTrialRemainingSeconds } =
    useGetCurrentPractitionerEntitlements();
  const {
    data: proTrialEntitlement,
    isLoading: isProTrialEntitlementLoading,
    error: proTrialEntitlementError,
  } = useGetProTrialEntitlement();

  const encountersRemaining = quota?.encounters?.remaining;
  const timeBasedQuotaDaysRemaining = timeQuota?.remaining_days;

  const isAuraProTrialExpired =
    isAuraProTrial && (auraProTrialRemainingSeconds ?? 1) <= 0;
  const isAuraProTrialNotExpired =
    isAuraProTrial && (auraProTrialRemainingSeconds ?? 1) > 0;

  const canViewDemo =
    isAuraProTrialNotExpired || (!isAuraPro && !!proTrialEntitlement?.entitled); // User is either pro trial or never been pro
  const canBookADemo =
    isBookDemoFeatureEntitled && isAuraPro && !isAuraProTrial; // only show book a demo for top tier pro users

  const canUpgradeToPro =
    isUpgradePlanFeatureEntitled &&
    (!isAuraPro || // User is free
      isAuraProTrial || // User is on pro trial
      isAuraProTrialExpired); // User's pro trial has expired

  if (isProTrialEntitlementLoading || proTrialEntitlementError) {
    return null;
  }

  return (
    <div className="flex items-center gap-3">
      {pathname === '/encounters' &&
        (typeof timeBasedQuotaDaysRemaining === 'number' ||
          typeof encountersRemaining === 'number') &&
        canCreateEncounter && (
          <Alert
            message={
              <>
                <div className="flex flex-row justify-between">
                  <div>
                    <span className="text-sm">
                      {typeof timeBasedQuotaDaysRemaining === 'number' ? (
                        timeBasedQuotaDaysRemaining === 0 ? (
                          'Trial expired'
                        ) : (
                          `${Math.max(0, timeBasedQuotaDaysRemaining)}${
                            !isMobileView ? ' days of free trial left' : ''
                          }`
                        )
                      ) : typeof encountersRemaining === 'number' ? (
                        <>
                          {Math.max(0, encountersRemaining - 1)}{' '}
                          {!isMobileView && 'encounters left'}
                        </>
                      ) : (
                        ''
                      )}
                    </span>
                    {typeof encountersRemaining === 'number' &&
                      encountersRemaining <= 0 &&
                      !isMobileView && (
                        <div
                          className="text-gray-500"
                          style={{ fontSize: '10px' }}
                        >
                          <CalendarOutlined className="mr-0.5 text-gray-500" />{' '}
                          Free encounters in {remainingDaysToNextMonth} days
                        </div>
                      )}
                  </div>
                  {((typeof timeBasedQuotaDaysRemaining === 'number' &&
                    timeBasedQuotaDaysRemaining <= 0) ||
                    (typeof encountersRemaining === 'number' &&
                      encountersRemaining <= 0)) && (
                    <div
                      className={`flex items-center ${
                        isMobileView ? '' : 'ml-4'
                      }`}
                    >
                      <Button
                        type="default"
                        size="small"
                        className="!bg-[#faad14] !text-white !border-[#faad14] hover:!bg-[#eda017] hover:!border-[#eda017]"
                        onClick={() => {
                          dispatch(isOPUpgradeModalVisibleSlice(true));
                        }}
                      >
                        <span className="text-sm">Upgrade</span>
                      </Button>
                    </div>
                  )}
                </div>
              </>
            }
            type={
              (typeof timeBasedQuotaDaysRemaining === 'number' &&
                timeBasedQuotaDaysRemaining > 0) ||
              (typeof encountersRemaining === 'number' &&
                encountersRemaining > 0)
                ? 'info'
                : 'warning'
            }
            showIcon
          />
        )}
      {canViewDemo && (
        <SupaDemo className={gradientButtonClass} isMobileView={isMobileView} />
      )}
      {canUpgradeToPro ? (
        <button
          className={gradientButtonClass}
          type="button"
          onClick={() => {
            analytics.track('Clicked Upgrade', { email: user?.email });
            setIsUpgradeModalVisible(true);
          }}
        >
          <ProIcon fill="#fff" forceFlashIcon={true} />
          {isMobileView ? null : 'Upgrade'}
        </button>
      ) : null}
      {canBookADemo ? (
        <button
          className={gradientButtonClass}
          type="button"
          onClick={() => {
            setOpenReclaim(true);
            analytics.track('Clicked Book Demo From Top Menu', {
              email: user?.email,
            });
          }}
        >
          <CallIcon fill="#fff" />
          {isMobileView ? null : 'Book a demo'}
        </button>
      ) : null}

      <ReclaimModal OpenReclaim={OpenReclaim} setOpenReclaim={setOpenReclaim} />
      <UpgradeModal
        isVisible={isUpgradeModalVisible}
        setIsVisible={setIsUpgradeModalVisible}
      />
    </div>
  );
};
